import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '@/lib/api/client';
import { useNavigate } from 'react-router-dom';
import { workflowTitles, type WorkflowModule } from '../workflowApi';
import { useStudyText } from '@/i18n/studyText';
import { useI18n } from '@/hooks/useI18n';

type Item = {
  id: string;
  kind: string;
  title: string;
  body: string;
  status: string;
  revision: number;
  group_id: string;
  deadline: string;
  starts_at?: string;
  section?: WorkflowModule;
  location: string;
  count: number;
  assigned: string[];
  completed: string[];
  history: { student_id: string; action: string; at: string; reason?: string }[];
};
type Group = { id: string; code: string; members: { id: string; name: string }[] };
type Base = {
  user_id: string;
  groups: Group[];
  leader: { group_id: string } | null;
  items: Item[];
};
export function DoctoralCoordinationPanel({
  advisor = false,
  kind,
  onClose,
}: {
  advisor?: boolean;
  kind: 'announcements' | 'duties';
  onClose: () => void;
}) {
  const { locale } = useI18n();
  const st = useStudyText();
  const navigate = useNavigate();
  const text = useCallback(
    (ru: string, kz: string, en: string) => (locale === 'kz' ? kz : locale === 'en' ? en : ru),
    [locale],
  );
  const [base, setBase] = useState<Base>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState<Record<string, string>>({});
  const [recipients, setRecipients] = useState<string[]>([]);
  const [startsAt, setStartsAt] = useState('');
  const [wholeGroup, setWholeGroup] = useState(false);
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [draft, setDraft] = useState({
    group_id: '',
    title: '',
    body: '',
    deadline: '',
    location: '',
    count: 1,
    publish: true,
    kind: 'ANNOUNCEMENT',
    section: '',
  });
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    apiRequest<Base>('/doctoral/coordination', { channel: 'platform' })
      .then((data) => {
        if (!cancelled) setBase(data);
      })
      .catch(() => {
        if (!cancelled) {
          setBase(undefined);
          setError(
            text(
              'Не удалось загрузить данные',
              'Деректерді жүктеу мүмкін болмады',
              'Unable to load data',
            ),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tick, locale, text]);
  const action = async (item: Item, action: string, student_ids: string[] = []) => {
    setBusy(true);
    setError('');
    try {
      await apiRequest(`/doctoral/coordination/${item.id}`, {
        channel: 'platform',
        method: 'PATCH',
        body: { revision: item.revision, action, student_ids, reason: reason[item.id] || '' },
      });
      setTick((t) => t + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : text('Ошибка', 'Қате', 'Error'));
      setTick((t) => t + 1);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="p-6 space-y-4">
      <button onClick={onClose}>{text('Назад', 'Артқа', 'Back')}</button>
      <h2>
        {kind === 'duties'
          ? text('Дежурства / мероприятия', 'Кезекшілік / іс-шаралар', 'Duties / events')
          : text(
              'Объявления и задания',
              'Хабарландырулар және тапсырмалар',
              'Announcements and tasks',
            )}
      </h2>
      <button disabled={busy} onClick={() => setTick((t) => t + 1)}>
        {text('Обновить', 'Жаңарту', 'Refresh')}
      </button>
      {error && <p role="alert">{error}</p>}
      {advisor && (
        <form
          className="grid gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError('');
            try {
              await apiRequest('/doctoral/coordination', {
                channel: 'platform',
                method: 'POST',
                body: {
                  ...draft,
                  section: draft.section || null,
                  whole_group: kind === 'duties' && wholeGroup,
                  starts_at: kind === 'duties' ? startsAt || null : null,
                  student_ids: kind === 'announcements' ? recipients : [],
                  kind: kind === 'duties' ? 'DUTY' : draft.kind,
                  request_id: requestId,
                  deadline: draft.deadline || null,
                },
              });
              setDraft({
                group_id: draft.group_id,
                title: '',
                body: '',
                deadline: '',
                location: '',
                count: 1,
                publish: true,
                kind: 'ANNOUNCEMENT',
                section: '',
              });
              setWholeGroup(false);
              setRecipients([]);
              setStartsAt('');
              setRequestId(crypto.randomUUID());
              setTick((t) => t + 1);
            } catch (e) {
              setError(e instanceof Error ? e.message : text('Ошибка', 'Қате', 'Error'));
            } finally {
              setBusy(false);
            }
          }}
        >
          <label>
            {text('Группа', 'Топ', 'Group')}
            <select
              required
              disabled={busy}
              value={draft.group_id}
              onChange={(e) => {
                setDraft({ ...draft, group_id: e.target.value });
                setRecipients([]);
                setRequestId(crypto.randomUUID());
              }}
            >
              <option value="">—</option>
              {base?.groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.code}
                </option>
              ))}
            </select>
          </label>
          <label>
            {text('Название', 'Атауы', 'Title')}
            <input
              required
              maxLength={250}
              disabled={busy}
              value={draft.title}
              onChange={(e) => {
                setDraft({ ...draft, title: e.target.value });
                setRequestId(crypto.randomUUID());
              }}
            />
          </label>
          <label>
            {text('Текст', 'Мәтін', 'Text')}
            <textarea
              maxLength={6000}
              disabled={busy}
              value={draft.body}
              onChange={(e) => {
                setDraft({ ...draft, body: e.target.value });
                setRequestId(crypto.randomUUID());
              }}
            />
          </label>
          {kind === 'announcements' && (
            <fieldset>
              <legend>
                {text(
                  'Получатели (без выбора — вся группа)',
                  'Алушылар (таңдалмаса — бүкіл топ)',
                  'Recipients (none selected means entire group)',
                )}
              </legend>
              {base?.groups
                .find((g) => g.id === draft.group_id)
                ?.members.map((m) => (
                  <label className="block" key={m.id}>
                    <input
                      type="checkbox"
                      disabled={busy}
                      checked={recipients.includes(m.id)}
                      onChange={(e) => {
                        setRecipients((old) =>
                          e.target.checked ? [...old, m.id] : old.filter((id) => id !== m.id),
                        );
                        setRequestId(crypto.randomUUID());
                      }}
                    />
                    {m.name}
                  </label>
                ))}
            </fieldset>
          )}
          {kind === 'announcements' && (
            <label>
              {text('Тип', 'Түрі', 'Type')}
              <select
                value={draft.kind}
                disabled={busy}
                onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
              >
                <option value="ANNOUNCEMENT">
                  {text('Объявление', 'Хабарландыру', 'Announcement')}
                </option>
                <option value="TASK">{text('Задание', 'Тапсырма', 'Task')}</option>
              </select>
            </label>
          )}
          {kind === 'announcements' && draft.kind === 'TASK' && (
            <label>
              {text('Раздел задания', 'Тапсырма бөлімі', 'Task section')}
              <select
                disabled={busy}
                value={draft.section}
                onChange={(e) => {
                  setDraft({ ...draft, section: e.target.value });
                  setRequestId(crypto.randomUUID());
                }}
              >
                <option value="">—</option>
                {Object.entries(workflowTitles).map(([id, title]) => (
                  <option key={id} value={id}>
                    {st(title)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            {text('Дата / срок', 'Күні / мерзімі', 'Date / deadline')}
            <input
              type="date"
              required={kind === 'duties'}
              disabled={busy}
              value={draft.deadline}
              onChange={(e) => {
                setDraft({ ...draft, deadline: e.target.value });
                setRequestId(crypto.randomUUID());
              }}
            />
          </label>
          {kind === 'duties' && (
            <>
              <label>
                {text('Время', 'Уақыты', 'Time')}
                <input
                  type="time"
                  disabled={busy}
                  value={startsAt}
                  onChange={(e) => {
                    setStartsAt(e.target.value);
                    setRequestId(crypto.randomUUID());
                  }}
                />
              </label>
              <label>
                <input
                  type="checkbox"
                  disabled={busy}
                  checked={wholeGroup}
                  onChange={(e) => {
                    setWholeGroup(e.target.checked);
                    setRequestId(crypto.randomUUID());
                  }}
                />
                {text('Назначить всю группу', 'Бүкіл топты тағайындау', 'Assign entire group')}
              </label>
              <label>
                {text('Место', 'Орны', 'Location')}
                <input
                  maxLength={300}
                  disabled={busy}
                  value={draft.location}
                  onChange={(e) => {
                    setDraft({ ...draft, location: e.target.value });
                    setRequestId(crypto.randomUUID());
                  }}
                />
              </label>
              <label>
                {text('Число участников', 'Қатысушылар саны', 'Number of people')}
                <input
                  type="number"
                  min={1}
                  disabled={busy}
                  value={draft.count}
                  onChange={(e) => {
                    setDraft({ ...draft, count: Number(e.target.value) });
                    setRequestId(crypto.randomUUID());
                  }}
                />
              </label>
            </>
          )}
          <label>
            <input
              type="checkbox"
              disabled={busy}
              checked={draft.publish}
              onChange={(e) => {
                setDraft({ ...draft, publish: e.target.checked });
                setRequestId(crypto.randomUUID());
              }}
            />
            {text('Опубликовать', 'Жариялау', 'Publish')}
          </label>
          <button disabled={busy}>{text('Сохранить', 'Сақтау', 'Save')}</button>
        </form>
      )}
      {base?.items
        .filter((i) => (kind === 'duties' ? i.kind === 'DUTY' : i.kind !== 'DUTY'))
        .map((item) => {
          const group = base.groups.find((g) => g.id === item.group_id);
          const canManage = advisor || base.leader?.group_id === item.group_id;
          return (
            <article key={item.id} className="border rounded p-4 space-y-2">
              <h3>{item.title}</h3>
              <p style={{ whiteSpace: 'pre-wrap' }}>{item.body}</p>
              <p>
                {item.deadline} {item.starts_at} {item.location}
              </p>
              <p>
                {item.status === 'DRAFT'
                  ? text('Черновик', 'Жоба', 'Draft')
                  : item.status === 'CLOSED'
                    ? text('Закрыто', 'Жабық', 'Closed')
                    : text('Опубликовано', 'Жарияланған', 'Published')}
              </p>
              {advisor && item.status === 'DRAFT' && (
                <button disabled={busy} onClick={() => void action(item, 'publish')}>
                  {text('Опубликовать', 'Жариялау', 'Publish')}
                </button>
              )}
              {advisor && item.status === 'PUBLISHED' && (
                <button disabled={busy} onClick={() => void action(item, 'close')}>
                  {text('Закрыть', 'Жабу', 'Close')}
                </button>
              )}
              {!advisor && item.section && (
                <button onClick={() => navigate('/app/doctoral/process/' + item.section)}>
                  {st(workflowTitles[item.section])}
                </button>
              )}
              {!advisor && item.kind !== 'DUTY' && item.status === 'PUBLISHED' && (
                <button disabled={busy} onClick={() => void action(item, 'acknowledge')}>
                  {text('Ознакомлен', 'Таныстым', 'Acknowledged')}
                </button>
              )}
              {item.kind === 'DUTY' && (
                <>
                  <p>
                    {text('Назначено', 'Тағайындалған', 'Assigned')}: {item.assigned.length} ·{' '}
                    {text('Выполнено', 'Орындалған', 'Completed')}: {item.completed.length}
                  </p>
                  {!advisor &&
                    item.status === 'PUBLISHED' &&
                    item.assigned.includes(base.user_id) &&
                    !item.completed.includes(base.user_id) && (
                      <label>
                        {text('Причина отказа', 'Бас тарту себебі', 'Decline reason')}
                        <input
                          value={reason[item.id] || ''}
                          maxLength={1000}
                          disabled={busy}
                          onChange={(e) =>
                            setReason((old) => ({ ...old, [item.id]: e.target.value }))
                          }
                        />
                        <button
                          disabled={busy || !reason[item.id]?.trim()}
                          onClick={() => void action(item, 'decline')}
                        >
                          {text(
                            'Отказаться от дежурства',
                            'Кезекшіліктен бас тарту',
                            'Decline duty',
                          )}
                        </button>
                      </label>
                    )}
                  {canManage && item.status === 'PUBLISHED' && (
                    <>
                      {(group?.members || []).map((member) => (
                        <label className="block" key={member.id}>
                          <input
                            type="checkbox"
                            disabled={busy}
                            checked={(selected[item.id] || item.assigned).includes(member.id)}
                            onChange={(e) =>
                              setSelected((old) => ({
                                ...old,
                                [item.id]: e.target.checked
                                  ? [...(old[item.id] || item.assigned), member.id]
                                  : (old[item.id] || item.assigned).filter(
                                      (id) => id !== member.id,
                                    ),
                              }))
                            }
                          />
                          {member.name}
                        </label>
                      ))}
                      <button
                        disabled={busy}
                        onClick={() =>
                          void action(item, 'assign', selected[item.id] || item.assigned)
                        }
                      >
                        {text('Сохранить дежурных', 'Кезекшілерді сақтау', 'Save duty assignments')}
                      </button>
                      <button
                        disabled={busy || !item.assigned.length}
                        onClick={() =>
                          void action(item, 'complete', selected[item.id] || item.assigned)
                        }
                      >
                        {text('Отметить выполнение', 'Орындалуын белгілеу', 'Mark completed')}
                      </button>
                    </>
                  )}
                  {item.history?.length > 0 && (
                    <details>
                      <summary>{text('История', 'Тарих', 'History')}</summary>
                      {item.history.map((h, i) => (
                        <p key={i}>
                          {h.at} ·{' '}
                          {group?.members.find((m) => m.id === h.student_id)?.name || h.student_id}{' '}
                          ·{' '}
                          {(
                            {
                              assigned: text('Назначен', 'Тағайындалды', 'Assigned'),
                              replaced: text('Заменён', 'Ауыстырылды', 'Replaced'),
                              completed: text('Выполнено', 'Орындалды', 'Completed'),
                              declined: text('Отказался', 'Бас тартты', 'Declined'),
                            } as Record<string, string>
                          )[h.action] || h.action}{' '}
                          {h.reason}
                        </p>
                      ))}
                    </details>
                  )}
                </>
              )}
            </article>
          );
        })}
      {base &&
        !base.items.some((i) => (kind === 'duties' ? i.kind === 'DUTY' : i.kind !== 'DUTY')) && (
          <p>{text('Записей пока нет', 'Әзірге жазбалар жоқ', 'No records yet')}</p>
        )}
    </section>
  );
}
